import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Star, X } from "lucide-react";
import styles from "./VoteReputation.module.scss";
import { getUserSkill, likeUser } from "../../../../store/actions";
import { useActionGuard } from "../../../../hooks/useActionGuard.ts";

const VoteReputation = ({ getUserSkill, userSkill, userCalificatedData, likeUser }) => {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [pendingSelection, setPendingSelection] = useState("");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const { guard } = useActionGuard();
  const targetUserId = userCalificatedData?.userData?.id;

  useEffect(() => {
    if (targetUserId) {
      getUserSkill(targetUserId);
    }
  }, [getUserSkill, targetUserId]);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setPendingSelection(value);
    if (!value) {
      return;
    }
    if (selectedSkills.includes(value)) {
      return;
    }
    setSelectedSkills((prev) => [...prev, value]);
    setPendingSelection("");
  };

  const removeSkill = (categoryId) => {
    setSelectedSkills((prev) => prev.filter((cat) => cat !== categoryId));
  };

  const handleVote = guard("send_calification", () => {
    const skillIds = selectedSkills
      .map((skill) => parseInt(skill, 10))
      .filter((skillId) => !Number.isNaN(skillId));
    const parsedRating = parseInt(String(rating), 10);
    const parsedUserId = parseInt(String(targetUserId), 10);
    if (Number.isNaN(parsedRating) || Number.isNaN(parsedUserId)) {
      return;
    }
    likeUser(parsedRating, comment.trim(), parsedUserId, skillIds);
  });

  const isFormDisabled = selectedSkills.length === 0;
  const commentDisabled = isFormDisabled;
  const canSubmit = !isFormDisabled && rating > 0 && comment.trim().length > 0;

  const selectedCategories = useMemo(
    () =>
      selectedSkills.map((skillId) => {
        const category = userSkill.find((skill) => String(skill.id) === skillId);
        return {
          id: skillId,
          name: category?.name ?? "Rubro",
        };
      }),
    [selectedSkills, userSkill],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.formGroup}>
        <label htmlFor="calification-category">Elegí rubros a calificar</label>
        <select
          id="calification-category"
          value={pendingSelection}
          onChange={handleCategoryChange}
          className={styles.select}
        >
          <option value="">Seleccionar rubro</option>
          {userSkill.map((category) => (
            <option key={category.id} value={String(category.id)}>
              {category.name}
            </option>
          ))}
        </select>
        {selectedCategories.length > 0 && (
          <div className={styles.selectedCategories}>
            {selectedCategories.map((category) => (
              <span key={category.id} className={styles.categoryTag}>
                {category.name}
                <button
                  type="button"
                  onClick={() => removeSkill(category.id)}
                  aria-label="Quitar rubro"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="calification-comment">Comentario</label>
        <textarea
          id="calification-comment"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Contanos cómo fue tu experiencia..."
          disabled={commentDisabled}
          className={styles.textarea}
        />
      </div>

      <div className={styles.formGroup}>
        <label>Calificación</label>
        <div className={styles.starRating}>
          {[1, 2, 3, 4, 5].map((star) => {
            const isActive = star <= (hoveredRating || rating);
            return (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                onClick={() => setRating(star)}
                disabled={isFormDisabled}
                className={styles.starButton}
              >
                <Star className={`${styles.starIcon} ${isActive ? styles.active : ""}`} />
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className={styles.submitButton}
        onClick={handleVote}
        disabled={!canSubmit}
      >
        Enviar calificación
      </button>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userSkill: state.register.userSkills,
  userCalificatedData: state.general.calificationModal,
});

export default connect(mapStateToProps, { getUserSkill, likeUser })(VoteReputation);
